import { useEffect, useState } from "react";
import { useSearch } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Store } from "lucide-react";

import { AccountShell } from "../../components/desktop/account-nav.tsx";
import { Button, Empty, ErrorBlock, Input, Spinner } from "../../components/ui.tsx";
import { orgFetch } from "../../api/org-client.ts";
import { useIdentities } from "../../api/queries.ts";
import type { ShopSelf } from "../shop-settings.tsx";

/**
 * ═══════════════════════════════════════════════════════════════
 *  网页版 · 店铺设置
 * ═══════════════════════════════════════════════════════════════
 *
 * 🔴 **说明文字挪到右边，表单在左边。**
 *    这一页每一项后面都跟着一段"为什么"（留空会怎样、为什么不填收款账号、
 *    加价率只是默认值）—— 手机上只能夹在输入框中间，一屏里字比框还多。
 *    网页上把它们排到右栏：**要填的东西连成一列**，解释在旁边随时可看。
 *
 * ⚠️ 表单初值要等数据回来再灌一次。只在 `useState` 里给初值的话，
 *    第一次渲染时数据还没到，输入框会永远是空的。
 */

/** 一组「左表单 + 右说明」。 */
function Field({
  title,
  children,
  note,
}: {
  title: string;
  children: React.ReactNode;
  note: React.ReactNode;
}) {
  return (
    <section className="grid grid-cols-[minmax(0,420px)_minmax(0,1fr)] items-start gap-8 border-b border-border py-6 first:pt-0 last:border-b-0">
      <div>
        <p className="mb-3 text-[15px] font-medium">{title}</p>
        {children}
      </div>
      <p className="pt-9 text-[13px] leading-relaxed text-muted-foreground">{note}</p>
    </section>
  );
}

export function DesktopShopSettings() {
  const search = useSearch({ from: "/shop-settings" }) as { orgId?: number };
  const { data: identities, isPending } = useIdentities();
  const qc = useQueryClient();

  const orgs = identities?.orgs ?? [];
  const orgId = search.orgId ?? orgs[0]?.id;

  const shop = useQuery({
    queryKey: ["merchant", "shop", orgId],
    queryFn: () => orgFetch<ShopSelf>("/shop", orgId!),
    enabled: Boolean(orgId),
  });

  const [displayName, setDisplayName] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [rate, setRate] = useState("");

  useEffect(() => {
    if (!shop.data) return;
    setDisplayName(shop.data.displayName);
    setContactName(shop.data.contactName);
    setContactPhone(shop.data.contactPhone);
    setRate(shop.data.markupRate === null ? "" : String(+(shop.data.markupRate * 100).toFixed(2)));
  }, [shop.data]);

  const saveShop = useMutation({
    mutationFn: (body: Partial<ShopSelf>) =>
      orgFetch("/shop", orgId!, { method: "PATCH", body: JSON.stringify(body) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["merchant", "shop", orgId] }),
  });

  const saveRate = useMutation({
    mutationFn: () =>
      orgFetch("/pricing/markup", orgId!, {
        method: "POST",
        body: JSON.stringify({ rate: Number(rate) / 100 }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["merchant", "shop", orgId] }),
  });

  /**
   * 🔴 **没有商家身份时不能只转圈。** disabled 的查询 `isPending` 恒为 true ——
   *    未登录、或名下没有店的人会看到一个永远停不下来的圈。
   */
  if (!isPending && !orgId)
    return (
      <AccountShell title="店铺设置">
        <Empty title="你还不是商家" hint="店铺设置是商家功能。想开店请联系上级商家或平台。" action={{ label: "回首页", to: "/" }} compact />
      </AccountShell>
    );
  if (isPending || shop.isPending)
    return (
      <AccountShell title="店铺设置">
        <Spinner compact />
      </AccountShell>
    );
  if (shop.error)
    return (
      <AccountShell title="店铺设置">
        <ErrorBlock error={shop.error} />
      </AccountShell>
    );

  const s = shop.data!;
  const phoneMissing = !s.contactPhone?.trim();

  return (
    <AccountShell title="店铺设置">
      {/*
        🔴 **待审 / 停业时所有经营动作都会被拦**（改价、发券、上架、发货…）。
           不把状态摆到他眼前的话，他只会看到一连串莫名其妙的报错。
      */}
      {s.status !== "active" && (
        <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50 px-5 py-4 text-[14px] leading-relaxed text-amber-800">
          {s.status === "pending"
            ? "资质还在审核中，通过之前改价、发券、上架、发货都会被拦下。请联系平台。"
            : s.status === "suspended"
              ? "店铺处于停业整顿状态，经营动作暂时都做不了。请联系平台。"
              : "店铺已清退。你账上的钱仍然可以提现，但不能再开展经营。"}
        </div>
      )}

      <div className="rounded-xl border border-border bg-card px-6">
        {/* ── ① 客户看到的店名 ── */}
        <Field
          title="客户看到的名字"
          note={
            <>
              留空就用工商名「{s.name}」。可以起个客户好记的名字，
              比如「王师傅建材」而不是「深圳市××建材有限公司」。
            </>
          }
        >
          <Input
            label="店铺名称"
            value={displayName}
            placeholder={s.name}
            onChange={(e) => setDisplayName(e.target.value)}
          />
          <Button
            className="mt-3"
            loading={saveShop.isPending}
            onClick={() => saveShop.mutate({ displayName })}
          >
            保存名称
          </Button>
        </Field>

        {/* ── ② 收款联系方式 ── */}
        <Field
          title="收款联系方式"
          note={
            <>
              客户下单后会看到这个，联系你付款。
              <br />
              {/*
                ⚠️ **只填联系方式，不填收款账号。** 账号变更频繁（换卡、换收款码），
                   存进系统里过期了就是把钱打给一个作废的账户；而且落库意味着
                   任何能读商家资料的人都能拿到你的账户。
              */}
              收款账号不用填在这儿 —— 电话里说更稳，也免得改了忘记同步。
            </>
          }
        >
          {/*
            🔴 **线下收款模式下这不是可选项。**
               没填的话买家的付款页上只有一句"联系商家"却不知道联系谁，
               这单大概率就丢了。
          */}
          {phoneMissing && (
            <p className="mb-3 rounded-lg bg-amber-50 px-3.5 py-2.5 text-[13px] leading-relaxed text-amber-800">
              还没填。客户下单后要联系你付款，没有电话他就只能干等 —— 这单大概率就丢了。
            </p>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="称呼"
              value={contactName}
              placeholder="王师傅"
              onChange={(e) => setContactName(e.target.value)}
            />
            <Input
              label="电话"
              value={contactPhone}
              placeholder="13800138000"
              inputMode="tel"
              onChange={(e) => setContactPhone(e.target.value)}
            />
          </div>
          <Button
            className="mt-3"
            loading={saveShop.isPending}
            onClick={() => saveShop.mutate({ contactName, contactPhone })}
          >
            保存联系方式
          </Button>
        </Field>

        {/* ── ③ 批量加价率 ── */}
        <Field
          title="批量加价率"
          note={
            <>
              {/*
                🔴 **售价是你自己定的，不是系统按百分比自动算的。**
                   不写清楚的话商家会以为价格是平台定的、自己只能调一个比例 ——
                   而这个平台最核心的能力恰恰是"每个商家自己的一套价格"。
              */}
              {s.markupRate === null
                ? "还没设过。设一个之后，你店里所有还没单独定价的商品都按这个比例卖。"
                : `当前 ${+(s.markupRate * 100).toFixed(2)}%。`}
              <br />
              这只是个省事的默认值 —— 在「我的定价」里单独改过的商品不受它影响。
            </>
          }
        >
          <div className="flex items-center gap-2">
            <Input
              inputMode="decimal"
              placeholder="15"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
              className="w-[140px]"
            />
            <span className="text-[14px] text-muted-foreground">%</span>
            <Button loading={saveRate.isPending} disabled={!rate} onClick={() => saveRate.mutate()}>
              保存
            </Button>
          </div>
          {saveRate.error && <ErrorBlock error={saveRate.error} />}
          {saveRate.isSuccess && <p className="mt-2 text-[13px] text-emerald-600">已生效</p>}
        </Field>
      </div>

      {saveShop.error && <ErrorBlock error={saveShop.error} />}

      {/* 客户视角预览 —— 改完名字最想做的下一件事就是"看看长什么样"。 */}
      <a
        href={`/shop/${s.id}`}
        className="mt-5 inline-flex items-center gap-2 rounded-xl border border-border bg-card px-5 py-3 text-[14px] transition-colors hover:border-brand-200"
      >
        <Store className="size-4 text-muted-foreground" />
        看看客户眼里的我的店
      </a>
    </AccountShell>
  );
}
